import type { AcademyLesson, AcademyArea } from '../../../pages/academy/Academy/AcademyService';
import {
  getTierSkills,
  isSkillComplete,
  isTierAvailable,
  isTierComplete,
} from './academyProgress';

export function getDefaultTierKey(area: AcademyArea | undefined): string {
  const firstIncompleteTier = area?.tiers.find((tier, index) => {
    return isSelectableTier(area, tier.tierKey, index) && !isTierComplete(area, tier.tierKey);
  });

  return (
    firstIncompleteTier?.tierKey ||
    area?.tiers.find((tier, index) => isSelectableTier(area, tier.tierKey, index))?.tierKey ||
    area?.tiers[0]?.tierKey ||
    ''
  );
}

export function getEffectiveTierKey(area: AcademyArea, selectedTierKey: string): string {
  const selectedTierIndex = area.tiers.findIndex((tier) => tier.tierKey === selectedTierKey);

  if (selectedTierIndex >= 0 && isTierAvailable(area, selectedTierIndex)) {
    return area.tiers[selectedTierIndex].tierKey;
  }

  return getDefaultTierKey(area);
}

export function getTierView(area: AcademyArea, selectedTierKey: string): AcademyArea {
  const skillPaths = area.skillPaths.map((path) => ({
    ...path,
    skills: path.skills.filter((skill) => skill.tierKeys.includes(selectedTierKey)),
  }));
  const tierSkills = skillPaths.flatMap((path) => path.skills);
  const recommendedLessonByKey = new Map(
    tierSkills.map((skill) => [skill.skillKey, skill]),
  );

  area.recommendedLessons.forEach((lesson) => {
    if (lesson.tierKeys.includes(selectedTierKey) && !recommendedLessonByKey.has(lesson.skillKey)) {
      recommendedLessonByKey.set(lesson.skillKey, lesson);
    }
  });

  return {
    ...area,
    recommendedLessons: Array.from(recommendedLessonByKey.values())
      .filter((skill) => !isSkillComplete(skill))
      .sort(compareRecommendedLessons)
      .slice(0, 3),
    skillPaths,
  };
}

export function findSkillInArea(
  area: AcademyArea,
  skillKey: string,
): AcademyLesson | undefined {
  return (
    area.skillPaths.flatMap((path) => path.skills).find((skill) => skill.skillKey === skillKey) ||
    area.recommendedLessons.find((lesson) => lesson.skillKey === skillKey)
  );
}

function compareRecommendedLessons(first: AcademyLesson, second: AcademyLesson): number {
  return (
    first.recommendedOrder - second.recommendedOrder ||
    first.displayOrder - second.displayOrder ||
    first.skillKey.localeCompare(second.skillKey)
  );
}

function isSelectableTier(area: AcademyArea, tierKey: string, tierIndex: number): boolean {
  return isTierAvailable(area, tierIndex) && getTierSkills(area, tierKey).length > 0;
}
